/**
 * Robust scroll-to-anchor with header offset and lazy-load resilience.
 *
 * Problem: Lazy-loaded sections cause the page height to grow after the
 * initial scroll, so `scrollIntoView` stops mid-page.
 *
 * Solution: We use `window.scrollTo` with a computed offset, then keep
 * retrying as long as either:
 *   - the element is still far from the viewport top, OR
 *   - the document height is still changing (sections loading).
 *
 * Optional: pass `{ highlight: true }` to briefly flash the target
 * element after scroll stabilization (used to draw attention).
 */
export interface ScrollToAnchorOptions {
  highlight?: boolean;
}

const HIGHLIGHT_CLASS = "anchor-highlight";
const HIGHLIGHT_DURATION = 1800;

function triggerHighlight(el: HTMLElement) {
  // Remove first so re-triggering restarts the animation
  el.classList.remove(HIGHLIGHT_CLASS);
  // Force reflow
  void el.offsetWidth;
  el.classList.add(HIGHLIGHT_CLASS);
  window.setTimeout(() => {
    el.classList.remove(HIGHLIGHT_CLASS);
  }, HIGHLIGHT_DURATION);
}

export function scrollToAnchor(
  id: string,
  maxDuration = 6000,
  options: ScrollToAnchorOptions = {},
) {
  const started = Date.now();
  let lastDocHeight = 0;
  let highlightFired = false;

  const getHeaderOffset = () =>
    (document.querySelector("header")?.offsetHeight ?? 80) + 12;

  const tryScroll = () => {
    const el = document.getElementById(id);
    if (!el) {
      // Element not in DOM yet — wait for lazy load
      if (Date.now() - started < maxDuration) {
        setTimeout(tryScroll, 200);
      }
      return;
    }

    const headerOffset = getHeaderOffset();
    const targetTop =
      el.getBoundingClientRect().top + window.pageYOffset - headerOffset;

    window.scrollTo({ top: targetTop, behavior: "smooth" });

    // Schedule a follow-up check
    const recheckDelay = 500;
    setTimeout(() => {
      const currentDocHeight = document.documentElement.scrollHeight;
      const rect = el.getBoundingClientRect();
      const docHeightChanged = currentDocHeight !== lastDocHeight;
      lastDocHeight = currentDocHeight;

      const stable =
        Math.abs(rect.top - headerOffset) <= 80 && !docHeightChanged;

      if (Date.now() - started >= maxDuration || stable) {
        // Scroll has either stabilized or timed out — fire highlight once
        if (options.highlight && !highlightFired) {
          highlightFired = true;
          triggerHighlight(el);
        }
        return;
      }

      // If the element is still far from the top or the page is still growing,
      // scroll again.
      tryScroll();
    }, recheckDelay);
  };

  lastDocHeight = document.documentElement.scrollHeight;
  tryScroll();
}
